import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface Adjustment {
  id: number;
  name: string;
  category: string;
  valueType: string;
  amount: number | string;
  isDeduction: boolean;
  [key: string]: unknown;
}

export interface PayrollEmployee {
  id: number;
  staffId: number;
  fullName: string;
  grossSalary: number | string;
  deductions: number | string;
  netSalary: number | string;
  adjustments?: Adjustment[];
  status?: string;
  [key: string]: unknown;
}

export interface Payroll {
  id: number | string;
  year: number;
  month: number;
  periodDisplay: string;
  status: 'draft' | 'pending' | 'completed' | 'published' | 'cancelled';
  employees: PayrollEmployee[];
  employeeCount: number;
  reportEnabled?: boolean;
  [key: string]: unknown;
}

export interface PayrollListResponse {
  data: Payroll[];
  meta: {
    years: number[];
    [key: string]: unknown;
  };
}

export const ListApi = createApi({
  reducerPath: 'ListApi',
  baseQuery,
  tagTypes: ['payroll'],
  endpoints: builder => ({
    getList: builder.query<
      PayrollListResponse,
      { year?: string; search?: string; staffId?: string; month?: string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('search', params.search);
        if (params?.year) queryParams.append('year', params.year);
        if (params?.staffId) queryParams.append('staff_id', params.staffId);
        if (params?.month) queryParams.append('month', params.month);

        return `/payroll/payrolls?${queryParams.toString()}`;
      },
      providesTags: ['payroll'],
    }),
    createList: builder.mutation<Payroll, Partial<Payroll>>({
      query: newList => ({
        url: '/payroll/payrolls',
        method: 'POST',
        body: newList,
      }),
      invalidatesTags: ['payroll'],
    }),
    updateList: builder.mutation<Payroll, { id: string; updates: Partial<Payroll> }>({
      query: ({ id, updates }) => ({
        url: `/payroll/payrolls/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['payroll'],
    }),
    getEmployeePayslip: builder.query<
      PayrollEmployee,
      { year: number; month: number; staffId: number }
    >({
      query: ({ year, month, staffId }) =>
        `/payroll/${year}-${month}/employees/${staffId}`,
      providesTags: ['payroll'],
    }),
  }),
});

export const {
  useGetListQuery,
  useCreateListMutation,
  useUpdateListMutation,
  useGetEmployeePayslipQuery,
} = ListApi;
